import { CardItemData } from '../../molecules/card-item/card-item';
import { resolveStrapiMediaUrl } from '../../../core/utils/strapi-media.util';
import { renderRichText } from '../../../core/utils/render-rich-text.util';

/**
 * Adapts a cards-section. The Strapi shape is a section wrapper
 * with a nested `cardsBlock` component containing `columns` and `cards`.
 * Also handles the `blocks.cards-block` shape which has cards inline.
 */
export function adaptCardsBlock(
  entry: Record<string, unknown>,
  baseUrl: string,
): Record<string, unknown> {
  // Cards can come from:
  // 1. entry.cardsBlock.cards (named section with nested component)
  // 2. entry.cards (direct cards-block in blocks array)
  const cardsBlock = entry['cardsBlock'] as Record<string, unknown> | undefined;
  const rawCards = cardsBlock?.['cards'] ?? entry['cards'] ?? [];

  const cards: CardItemData[] = Array.isArray(rawCards)
    ? rawCards.map((c: Record<string, unknown>) => {
        const img = (c['imageSrc'] ?? c['image']) as Record<string, unknown> | null;
        const linkObj = c['link'] as Record<string, unknown> | null;
        const decorator = c['decorator'] as Record<string, unknown> | null;
        const rawVariant = (c['variant'] as CardItemData['variant']) ?? 'base';
        const variant = rawVariant === 'base' && img ? 'image' : rawVariant;
        const subhead = (c['subhead'] as string) ?? (c['title'] as string) ?? '';
        const heading = (c['heading'] as string) ?? undefined;

        return {
          variant,
          theme: (c['theme'] as CardItemData['theme']) ?? undefined,
          // For headed cards: subhead goes into the colored header bar (heading)
          subhead: variant === 'headed' && !heading ? '' :
            // Skip subhead when decorator already shows the same value
            (decorator && decorator['value'] === subhead ? '' : subhead),
          heading: variant === 'headed' && !heading ? subhead : heading,
          body: renderRichText((c['body'] as string) ?? (c['description'] as string) ?? ''),
          link: linkObj ? {
            href: (linkObj['href'] as string) ?? '#',
            text: (linkObj['text'] as string) ?? 'Learn more',
            style: (linkObj['style'] as 'arrow' | 'icon' | 'text') ?? 'arrow',
          } : undefined,
          imageSrc: typeof img === 'object' && img ? resolveStrapiMediaUrl(baseUrl, (img['url'] as string) ?? null) ?? '' : '',
          // imageAlt fallback chain (quick-102 — mirrors quick-100's archive-page.utils.ts fix):
          //   Strapi `imageSrc.alternativeText` (server-resolved by Phase 42 plan 42-04
          //   resolve-image-alt.js where applicable) → empty/whitespace → fall back to the
          //   card `subhead` (already rendered as <h3 class="s__card-item__subhead">).
          //
          //   `||` (not `??`) deliberately catches both null/undefined AND empty-string;
          //   `.trim()` catches Strapi's whitespace-only failure mode (editor types " " into
          //   the alt field). When `img` itself is missing, ship `''` — the molecule template
          //   skips the <img> element on empty imageSrc per card-item.html:12.
          //
          //   Defuses Lighthouse / axe-core `image-alt` (WCAG H30) + `link-name` (WCAG H67)
          //   on every clickable image-variant cards-block card across CMS pages
          //   (e.g. /platform/what-we-monitor/submerged-sewage-pumps where the user reported
          //   the live <img alt=""> regression).
          imageAlt: typeof img === 'object' && img
            ? (((img['alternativeText'] as string) || '').trim() || subhead)
            : '',
          imageWidth: typeof img === 'object' && img ? (img['width'] as number) ?? 0 : 0,
          imageHeight: typeof img === 'object' && img ? (img['height'] as number) ?? 0 : 0,
          topRightLabel: (c['topRightLabel'] as string) ?? undefined,
          footer: (c['footer'] as string) ?? undefined,
          highlight: (c['highlight'] as boolean) ?? false,
          decorator: decorator ? (() => {
            // decorator.src may be either a plain string path (text field) or
            // a Strapi media object ({ url, alternativeText, ... }). Normalise
            // both through resolveStrapiMediaUrl so relative /uploads paths
            // pick up the Strapi base URL.
            const rawSrc = decorator['src'] as string | Record<string, unknown> | null | undefined;
            let srcPath: string | null = null;
            let srcAlt = '';
            let srcWidth = 0;
            let srcHeight = 0;
            if (typeof rawSrc === 'string') {
              srcPath = rawSrc;
            } else if (rawSrc && typeof rawSrc === 'object') {
              srcPath = (rawSrc['url'] as string) ?? null;
              srcAlt = (rawSrc['alternativeText'] as string) ?? '';
              srcWidth = (rawSrc['width'] as number) ?? 0;
              srcHeight = (rawSrc['height'] as number) ?? 0;
            }
            return {
              type: (decorator['type'] as 'number' | 'label' | 'icon' | 'photo') ?? 'number',
              value: (decorator['value'] as string) ?? '',
              style: (decorator['style'] as 'colored' | 'circled' | 'square') ?? 'colored',
              svgContent: (decorator['svgContent'] as string) ?? '',
              src: resolveStrapiMediaUrl(baseUrl, srcPath) ?? '',
              // For photo decorators (person headshots, partner logos), fall back to
              // the card subhead when Strapi alternativeText is empty/whitespace.
              // Mirrors the quick-102 imageAlt fallback chain. Editorial alt still
              // wins; non-photo types (number/label/icon) are unaffected.
              alt: ((srcAlt || '').trim() || (decorator['type'] === 'photo' ? subhead : '')),
              width: srcWidth,
              height: srcHeight,
            };
          })() : undefined,
        } satisfies CardItemData;
      })
    : [];

  const columns = Number(cardsBlock?.['columns'] ?? entry['columns'] ?? 3);

  return {
    columns: columns as 2 | 3 | 4 | 5,
    cards,
  };
}
